import * as React from "react";
import { useMemo } from "react";
import { Redirect, Route, RouteComponentProps, Switch, useHistory } from "react-router-dom";
import { editorQueueListUrl, URLS, useSocialCoachSession } from "./utils";
import { useAnalyticsPageView } from "./utils/gtag";
import { RouteType, Types } from "@socialcoach/services";
import {
  AddPlayer,
  AdminLeaderboards,
  AlertsSection,
  CalendarPage,
  CampaignPage,
  Campaigns,
  EditCampaignPage,
  CreateAssistant,
  EditAssistant,
  EditPlayer,
  FirstLoginPage,
  SetPlayerPicture,
  FirstSocialNetworksPage,
  SettingsSection,
  PaymentInfo,
  FirstCongratsPage,
  PlayerSettings,
  EditorVideoProjectPage,
  ChangePasswordForm,
  CompliancePage,
  Dashboard,
  EditorEditingQueueList,
  EmbeddedPlayerSocialAccounts,
  Home,
  Library,
  LoginPage,
  SSOLoginPage,
  NotFoundPage,
  Users,
  PlayerVideoProjectPage,
  PlayerVideoScriptPage,
  RecordVideoPage,
  ResendPage,
  PlayerCardSettings,
  VerificationPage,
  VideoProjectDetailsPage,
  VideoScriptPage,
  VideoScriptsPage,
  PlayerDashboard,
  PlayerLibrary,
  PlayerBlogPage,
  UsersPage,
  ToolsPage,
  PkceAuthorization,
  ScreenCapturePage,
  ReviewResponsePage,
  LogoutPage,
  DesignStudioListPage,
  NewDesignPage,
  DesignEditorPage,
  BackgroundsPage,
  AdminDesignStudio,
} from "./containers";

import { AppContextProvider, AppContext } from "./providers";
import { CoachSettings, EnvValues, PricePlanTier, WebComponents } from "./interfaces";
import MetaTags from "./components/MetaTags";

import { SCSessionRoute } from "./routers/SessionRoute";
import {
  CoachComponentGuard,
  ScriptAccessGuard,
  PlayerSettingsGuard,
  PlayerPlusGuard,
} from "./routers/SessionRoute/RouteAccessControl";
import { LegacyRedirectRoute } from "./routers/LegacyRedirectRoute";
import { getLegacyRedirectRules } from "./legacy/legacyRedirects";

import { EnvContext } from "./contexts/appContexts";
import { Reputation } from "@/containers/Coach/Reputation";
import { PlayerReputation } from "@/containers/Players/Reputation";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { MfaChallengeProvider } from "./providers/MfaChallengeProvider";
import { GlobalAdminShortcuts } from "./components/GlobalAdminShortcuts";
import { Footer } from "@/components/Footer";

const RouteChangeListener: React.FC = () => {
  const history = useHistory();
  React.useEffect(() => {
    const unlisten = history.listen((location, action) => {
      console.log(`🚀 Route Changed to: ${location.pathname}${location.search} via action: ${action}`);
    });
    return unlisten;
  }, [history]);
  return null;
};

interface PageProps extends RouteComponentProps {
  title?: string;
  includeHeader?: boolean;
  includeFooter?: boolean;
  Component: any;
  envValues?: EnvValues;
  type?: string;
  isPlayer?: boolean;
  [key: string]: any;
}

export const Page = ({
  location,
  title = "",
  includeHeader = true,
  includeFooter = true,
  Component,
  ...props
}: PageProps) => {
  const { pathname, search } = location;
  useAnalyticsPageView(pathname + search);

  const pageTitle = useMemo(() => {
    return title ? `${title} - SocialCoach` : "SocialCoach";
  }, [title]);

  return (
    <div className="flex-1 flex flex-col h-full w-full relative">
      <MetaTags title={pageTitle} />

      <main className="flex-auto flex flex-col w-full">
        <Component {...props} />
      </main>

      {includeFooter && <Footer />}
    </div>
  );
};

export default (envValues: EnvValues) => {
  return (
    <AppContextProvider>
      <MfaChallengeProvider>
        {import.meta.env.DEV && import.meta.env.VITE_SHOW_ROUTE_CHANGES === "true" && <RouteChangeListener />}
        <ImpersonationBanner />
        <GlobalAdminShortcuts />
        <AppContext.Consumer>
          {({ session }) => (
            <Switch>
              {/* ALL USER PAGES */}
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.forgotPassword}
                render={(props: RouteComponentProps) => (
                  <Page {...props} type="ForgotPassword" includeHeader={false} Component={ResendPage} />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.changePassword}
                render={(props: RouteComponentProps) => (
                  <Page {...props} includeHeader={false} Component={ChangePasswordForm} />
                )}
              />

              {/* ALL USER PAGES END */}

              {/* LEGACY REDIRECTS */}
              {getLegacyRedirectRules(session).map(rule => (
                <Route
                  key={rule.path}
                  exact
                  path={rule.path}
                  render={(routeProps: any) => (
                    <LegacyRedirectRoute
                      {...routeProps}
                      getTo={(session, rp) => {
                        const params = (rp.match?.params ?? {}) as Record<string, string>;
                        const to = rule.getTo(session, params);
                        return `${to}${rp.location.search ?? ""}${rp.location.hash ?? ""}`;
                      }}
                    />
                  )}
                />
              ))}

              {/* COACH PAGES */}
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.home}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={Home} includeHeader={false} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.login}
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={(pageProps: any) => <LoginPage {...pageProps} />} includeHeader={false} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.signInSSO}
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={SSOLoginPage} includeHeader={false} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.activatePlayerUser}
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={VerificationPage} isPlayer={true} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.activateUser}
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={VerificationPage} includeHeader={false} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.resendEmail}
                render={(props: RouteComponentProps) => <Page {...props} Component={ResendPage} />}
              />

              {/** PLAYER PRIVATE PAGES */}
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.dashboard}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={PlayerDashboard} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.blogHome}
                reservedGroups={["PLAYER"]}
                render={() => <Redirect to={URLS.player.blogs} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.blog}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={PlayerBlogPage} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.libraryTab}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={PlayerLibrary} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                path={URLS.player.calendar}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={CalendarPage} />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                path={URLS.player.playerCards}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <PlayerSettingsGuard
                    routeProps={props}
                    envValues={envValues}
                    session={session}
                    Component={PlayerCardSettings}
                    setting={CoachSettings.ALLOW_PLAYER_ACCESS_TO_PLAYER_CARDS}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                path={URLS.player.reputation.respond}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <PlayerSettingsGuard
                    routeProps={props}
                    envValues={envValues}
                    session={session}
                    Component={ReviewResponsePage}
                    setting={CoachSettings.PLAYER_REPUTATION_MANAGEMENT}
                    includeHeader={false}
                    includeFooter={false}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                path={URLS.player.reputation.tab}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <PlayerSettingsGuard
                    routeProps={props}
                    envValues={envValues}
                    session={session}
                    Component={PlayerReputation}
                    setting={CoachSettings.PLAYER_REPUTATION_MANAGEMENT}
                    includeFooter={false}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.firstLogin}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) =>
                  session?.userRole == "PLAYER" ? (
                    <Page {...props} envValues={envValues} Component={FirstLoginPage} />
                  ) : (
                    <Page {...props} envValues={envValues} Component={NotFoundPage} />
                  )
                }
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.firstCongrats}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) =>
                  session?.userRole == "PLAYER" ? (
                    <Page {...props} envValues={envValues} Component={FirstCongratsPage} />
                  ) : (
                    <Page {...props} envValues={envValues} Component={NotFoundPage} />
                  )
                }
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.firstPicture}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={SetPlayerPicture} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.firstSocialNetworks}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={FirstSocialNetworksPage} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.settings}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={PlayerSettings} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.embeddedSocialAccounts}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <Page
                    {...props}
                    Component={EmbeddedPlayerSocialAccounts}
                    includeHeader={false}
                    includeFooter={false}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.paymentInfo}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={PaymentInfo} />}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.videoScript}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={props}
                    Component={PlayerVideoScriptPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.recordVideo}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={{ ...props, includeHeader: false, includeFooter: false }}
                    Component={RecordVideoPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.screenCapture}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={{ ...props, includeHeader: false, includeFooter: false }}
                    Component={ScreenCapturePage}
                    session={session}
                    envValues={envValues}
                    additionalSettings={[CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE]}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.newVideoScript}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={props}
                    Component={PlayerVideoScriptPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                path={URLS.player.newVideoProject}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => {
                  const isSocialCoachPlus = session?.subscription?.tier === PricePlanTier.PLAYER_PLUS;

                  return isSocialCoachPlus ? (
                    <Page {...props} envValues={envValues} Component={PlayerVideoProjectPage} />
                  ) : (
                    <Page {...props} envValues={envValues} Component={NotFoundPage} />
                  );
                }}
              />

              {/** COACH PRIVATE PAGES */}
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.dashboard}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => <Page {...props} Component={Dashboard} />}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.alerts}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.ALERTS}
                    Component={AlertsSection}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.newAssistant}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.ASSISTANTS}
                    Component={CreateAssistant}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.newPlayer}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.PLAYERS}
                    Component={AddPlayer}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.editAssistant}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.ASSISTANTS}
                    Component={EditAssistant}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.designStudio}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.DYNAMIC_PROMPTS]}
                    Component={DesignStudioListPage}
                    routeProps={{ ...props, includeFooter: false }}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.newDesign}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.DYNAMIC_PROMPTS]}
                    Component={NewDesignPage}
                    routeProps={{ ...props, includeFooter: false }}
                    envValues={envValues}
                  />
                )}
              />
              {/* Before the editor: "backgrounds" would otherwise read as a package id. */}
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.designStudioBackgrounds}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.DYNAMIC_PROMPTS]}
                    Component={BackgroundsPage}
                    routeProps={{ ...props, includeFooter: false }}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.design}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.DYNAMIC_PROMPTS]}
                    Component={DesignEditorPage}
                    routeProps={{ ...props, includeFooter: false }}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.libraryTabWithId}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[
                      WebComponents.PROMPTS,
                      WebComponents.MANAGE_SCRIPT_LIBRARY,
                      WebComponents.VIDEO_CATALYST,
                      WebComponents.VIDEO_PAGES,
                    ]}
                    Component={Library}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.newCampaigns}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.CAMPAIGNS}
                    Component={CampaignPage}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.campaigns}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.CAMPAIGNS}
                    Component={Campaigns}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.editCampaigns}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.CAMPAIGNS}
                    Component={EditCampaignPage}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.usersTab}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.PLAYERS, WebComponents.ASSISTANTS]}
                    Component={Users}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={[URLS.coach.reputation.formRoutes, URLS.coach.reputation.tab]}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.REPUTATION_MANAGEMENT]}
                    Component={Reputation}
                    routeProps={{ ...props, includeFooter: false, includeHeader: false }}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.editPlayerTab}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.PLAYERS}
                    Component={EditPlayer}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.calendar}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={CalendarPage} />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.playerCards}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.PLAYER_CARDS}
                    Component={PlayerCardSettings}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.compliance}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={WebComponents.COMPLIANCE}
                    Component={CompliancePage}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.settingsTab}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <CoachComponentGuard
                    componentName={[WebComponents.APP_SETTINGS, WebComponents.API_KEYS]}
                    Component={SettingsSection}
                    routeProps={props}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.recordVideo}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={{ ...props, includeHeader: false, includeFooter: false }}
                    Component={RecordVideoPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.coach.screenCapture}
                reservedGroups={["COACH"]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={{ ...props, includeHeader: false, includeFooter: false }}
                    Component={ScreenCapturePage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />

              {/* COACH PAGES END*/}

              {/* COACH/PLAYER PAGES */}
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.player.videoProject}
                reservedGroups={["PLAYER"]}
                render={(props: RouteComponentProps) => (
                  <PlayerPlusGuard
                    routeProps={props}
                    envValues={envValues}
                    session={session}
                    Component={VideoProjectDetailsPage}
                  />
                )}
              />
              {/* COACH/PLAYER PAGES END*/}

              {/* ADMIN PAGES INIT*/}
              <SCSessionRoute
                routeType={RouteType.PUBLIC_ROUTE}
                exact
                path={URLS.admin.login}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={(pageProps: any) => <LoginPage {...pageProps} />} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.dashboard}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => <Page {...props} Component={UsersPage} />}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.leaderboards}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => <Page {...props} Component={AdminLeaderboards} />}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.tools}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => <Page {...props} Component={ToolsPage} />}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.designStudio}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} includeFooter={false} Component={AdminDesignStudio} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.adminNewDesign}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} includeFooter={false} Component={AdminDesignStudio} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.designStudioBackgrounds}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} includeFooter={false} Component={AdminDesignStudio} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.adminDesign}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} includeFooter={false} Component={AdminDesignStudio} />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.admin.coachCalendar}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => <Page {...props} Component={CalendarPage} />}
              />
              {/* ADMIN PAGES END*/}

              {/* EDITOR PAGES */}
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.editor.videoProjects.root}
                reservedGroups={["EDITOR", "ADMIN"]}
                render={(props: RouteComponentProps) => (
                  <Redirect
                    exact
                    to={`${editorQueueListUrl()}${props.location.search ?? ""}${props.location.hash ?? ""}`}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={[URLS.editor.videoProjects.grid, URLS.editor.videoProjects.board]}
                reservedGroups={["EDITOR", "ADMIN"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={EditorEditingQueueList} />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.editor.videoProjects.detail}
                reservedGroups={["EDITOR", "ADMIN"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={EditorVideoProjectPage} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.editor.videoScripts}
                reservedGroups={["EDITOR", "ADMIN"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={VideoScriptsPage} />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.editor.videoScript}
                reservedGroups={["EDITOR", "ADMIN"]}
                render={(props: RouteComponentProps) => (
                  <Page {...props} envValues={envValues} Component={VideoScriptPage} />
                )}
              />
              {/* EDITOR PAGES END */}

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.videoScripts}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={props}
                    Component={VideoScriptsPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.videoScript}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={props}
                    Component={VideoScriptPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />
              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.newVideoScript}
                reservedGroups={[Types.UserGroups.ADMIN]}
                render={(props: RouteComponentProps) => (
                  <ScriptAccessGuard
                    routeProps={props}
                    Component={VideoScriptPage}
                    session={session}
                    envValues={envValues}
                  />
                )}
              />

              <SCSessionRoute
                routeType={RouteType.PRIVATE_ROUTE}
                exact
                path={URLS.pkceAuth}
                reservedGroups={[Types.UserGroups.ADMIN, Types.UserGroups.USER, "COACH", "PLAYER", "EDITOR"]}
                render={(props: RouteComponentProps) => (
                  <Page
                    {...props}
                    Component={(pageProps: any) => <PkceAuthorization {...pageProps} />}
                    includeHeader={false}
                  />
                )}
              />

              {/* NO SESSION PAGES */}
              <Route path={URLS.logout} exact component={LogoutPage} />
              <Route
                path={URLS.notFound}
                exact
                render={(props: RouteComponentProps) => (
                  <Page {...props} Component={NotFoundPage} includeHeader={false} />
                )}
              />
            </Switch>
          )}
        </AppContext.Consumer>
      </MfaChallengeProvider>
    </AppContextProvider>
  );
};
